/**
 * rakuzan-cart-limit.js
 *
 * Singapore cart limit enforcement.
 *
 * Liquid in main-cart-footer.liquid handles the initial disabled state. This
 * file only mirrors that state after AJAX cart updates, and blocks checkout
 * submit only when the last known cart state is already over the limit.
 */
(function () {
  'use strict';

  const DEFAULT_AMOUNT_LIMIT_SUBUNITS = 4500000;
  const DEFAULT_WEIGHT_LIMIT_GRAMS = 5000;

  let amountLimitSubunits = DEFAULT_AMOUNT_LIMIT_SUBUNITS;
  let weightLimitGrams = DEFAULT_WEIGHT_LIMIT_GRAMS;
  let lastKnownAmountExceeded = false;
  let lastKnownWeightExceeded = false;
  let lastKnownAmountExceededBy = 0;
  let lastKnownWeightExceededBy = 0;
  let isVerifyingCheckout = false;
  let bypassNextSubmit = false;
  let cartFooterSection;

  function readNumber(value, fallback) {
    if (value === undefined) return fallback;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function readLimitSettings() {
    const footer = document.getElementById('main-cart-footer');
    if (!footer) return;

    amountLimitSubunits = readNumber(footer.dataset.rzCartAmountLimit, DEFAULT_AMOUNT_LIMIT_SUBUNITS);
    weightLimitGrams = readNumber(footer.dataset.rzCartWeightLimit, DEFAULT_WEIGHT_LIMIT_GRAMS);
  }

  function computeState(cart) {
    const totalPrice = cart?.total_price || 0;
    const totalWeight = cart?.total_weight || 0;

    return {
      amountExceeded: amountLimitSubunits > 0 && totalPrice > amountLimitSubunits,
      weightExceeded: weightLimitGrams > 0 && totalWeight > weightLimitGrams,
      amountExceededBy: Math.max(0, totalPrice - amountLimitSubunits),
      weightExceededBy: Math.max(0, totalWeight - weightLimitGrams),
      cartIsEmpty: (cart?.item_count || 0) === 0,
    };
  }

  function readDesiredQuantity(input) {
    const parsed = Number(input.value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  function computeSubmitState(cart, form) {
    const quantityInputs = [...form.querySelectorAll('.quantity__input[data-index]')];
    if (!quantityInputs.length) return computeState(cart);

    let hasPendingQuantityChange = false;
    let projectedPrice = 0;
    let projectedWeight = 0;

    cart.items.forEach((item, index) => {
      const input = quantityInputs.find((candidate) => Number(candidate.dataset.index) === index + 1);
      const desiredQuantity = input ? readDesiredQuantity(input) : null;
      const cartQuantity = item.quantity || 0;
      const quantity = desiredQuantity === null ? cartQuantity : desiredQuantity;
      const unitPrice = item.final_price ?? item.price ?? (cartQuantity > 0 ? item.final_line_price / cartQuantity : 0);
      const unitWeight = item.grams || 0;

      if (desiredQuantity !== null && desiredQuantity !== cartQuantity) {
        hasPendingQuantityChange = true;
      }

      projectedPrice += unitPrice * quantity;
      projectedWeight += unitWeight * quantity;
    });

    if (!hasPendingQuantityChange) return computeState(cart);

    return {
      amountExceeded: amountLimitSubunits > 0 && projectedPrice > amountLimitSubunits,
      weightExceeded: weightLimitGrams > 0 && projectedWeight > weightLimitGrams,
      amountExceededBy: Math.max(0, projectedPrice - amountLimitSubunits),
      weightExceededBy: Math.max(0, projectedWeight - weightLimitGrams),
      cartIsEmpty: projectedWeight === 0 && projectedPrice === 0,
    };
  }

  function getLimitElements() {
    return {
      btn: document.getElementById('checkout'),
      dynBtns: document.getElementById('rz-dynamic-btns'),
      errEl: document.getElementById('rz-cart-limit-error'),
    };
  }

  function formatJPY(subunits) {
    return `¥${Math.round(subunits / 100).toLocaleString('en-US')} JPY`;
  }

  function formatKg(grams) {
    return `${(grams / 1000).toFixed(1).replace(/\.0$/, '')} kg`;
  }

  function createWarningLine(message) {
    const line = document.createElement('p');
    const icon = document.createElement('span');
    const text = document.createElement('span');

    line.className = 'rz-cart-limit-error__line';
    icon.className = 'rz-cart-limit-error__icon';
    icon.setAttribute('aria-hidden', 'true');
    text.textContent = message;
    line.append(icon, text);

    return line;
  }

  function renderCartLimitError(errEl, state) {
    errEl.replaceChildren();

    if (!state.amountExceeded && !state.weightExceeded) return;

    if (state.amountExceeded) {
      errEl.append(createWarningLine(`Order limit exceeded by ${formatJPY(state.amountExceededBy)}.`));
    }

    if (state.weightExceeded) {
      errEl.append(createWarningLine(`Weight limit exceeded by ${formatKg(state.weightExceededBy)}.`));
    }

    const helper = document.createElement('p');
    helper.className = 'rz-cart-limit-error__helper';
    helper.textContent = 'Please remove some items to stay within the limit and proceed to checkout.';
    errEl.append(helper);
  }

  function updateButtonState(state) {
    const { btn, dynBtns, errEl } = getLimitElements();
    const exceeded = state.amountExceeded || state.weightExceeded;
    const shouldDisable = state.cartIsEmpty || exceeded;

    lastKnownAmountExceeded = state.amountExceeded;
    lastKnownWeightExceeded = state.weightExceeded;
    lastKnownAmountExceededBy = state.amountExceededBy || 0;
    lastKnownWeightExceededBy = state.weightExceededBy || 0;

    if (btn) {
      btn.disabled = shouldDisable;
      btn.setAttribute('aria-disabled', String(shouldDisable));
    }

    if (dynBtns) {
      dynBtns.style.display = exceeded ? 'none' : '';
    }

    if (errEl) {
      renderCartLimitError(errEl, state);
      errEl.hidden = !exceeded;
    }
  }

  async function refreshState() {
    readLimitSettings();

    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      updateButtonState(computeState(cart));
    } catch (_e) {
      // Keep the Liquid-rendered initial state if the network check fails.
    }
  }

  function setCheckoutPending(isPending) {
    const { btn } = getLimitElements();
    if (!btn) return;

    btn.classList.toggle('loading', isPending);
    btn.setAttribute('aria-busy', String(isPending));
  }

  function showCartLimitError() {
    const { errEl } = getLimitElements();
    if (!errEl) return;

    renderCartLimitError(errEl, {
      amountExceeded: lastKnownAmountExceeded,
      weightExceeded: lastKnownWeightExceeded,
      amountExceededBy: lastKnownAmountExceededBy,
      weightExceededBy: lastKnownWeightExceededBy,
    });
    errEl.hidden = false;
    errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function showVerificationError() {
    const { errEl } = getLimitElements();
    if (!errEl) return;

    errEl.replaceChildren(createWarningLine('Unable to verify your cart right now. Please try again.'));
    errEl.hidden = false;
    errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function continueCheckout(form, submitter) {
    bypassNextSubmit = true;

    if (typeof form.requestSubmit === 'function' && submitter && submitter.form === form && !submitter.disabled) {
      form.requestSubmit(submitter);
      return;
    }

    if (submitter && submitter.name) {
      const hiddenSubmitter = document.createElement('input');
      hiddenSubmitter.type = 'hidden';
      hiddenSubmitter.name = submitter.name;
      hiddenSubmitter.value = submitter.value || submitter.textContent || 'checkout';
      form.append(hiddenSubmitter);
    }

    HTMLFormElement.prototype.submit.call(form);
  }

  async function handleSubmit(e) {
    const form = e.target;
    if (!form) return;

    const isCartForm = form.id === 'cart' || (form.action && form.action.indexOf('/checkout') !== -1);
    if (!isCartForm) return;

    if (bypassNextSubmit) {
      bypassNextSubmit = false;
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (isVerifyingCheckout) return;

    isVerifyingCheckout = true;
    setCheckoutPending(true);

    try {
      readLimitSettings();
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      const state = computeSubmitState(cart, form);
      updateButtonState(state);

      if (state.amountExceeded || state.weightExceeded || state.cartIsEmpty) {
        showCartLimitError();
        return;
      }

      continueCheckout(form, e.submitter || document.getElementById('checkout'));
    } catch (_e) {
      showVerificationError();
    } finally {
      isVerifyingCheckout = false;
      const stillExceeded = lastKnownAmountExceeded || lastKnownWeightExceeded;
      if (!stillExceeded) {
        setCheckoutPending(false);
      }
    }
  }

  function handleCartUpdate(event) {
    readLimitSettings();

    if (event && event.cartData) {
      updateButtonState(computeState(event.cartData));
    } else {
      refreshState();
    }
  }

  function layoutCartPage() {
    if (!document.body.classList.contains('template-cart')) return;

    const cartForm = document.getElementById('cart');
    const footerSection = cartFooterSection || document.querySelector('.cart__footer-wrapper');

    if (!cartForm || !footerSection || cartForm.closest('.rakuzan-cart-layout')) return;

    cartFooterSection = footerSection;

    const layout = document.createElement('div');
    layout.className = 'rakuzan-cart-layout';
    cartForm.before(layout);
    layout.append(cartForm, footerSection);
  }

  function resetMobileViewportAfterNoteInput(event) {
    if (!document.body.classList.contains('template-cart')) return;
    if (!event.target || event.target.id !== 'Cart-note') return;

    window.setTimeout(() => {
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
      window.scrollTo({ top: window.scrollY, left: 0, behavior: 'smooth' });
    }, 250);
  }

  function init() {
    readLimitSettings();
    layoutCartPage();
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('blur', resetMobileViewportAfterNoteInput, true);

    refreshState();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        handleCartUpdate(event);
        layoutCartPage();
      });
    }

    document.addEventListener('cart:change', refreshState);

    const bodyObserver = new MutationObserver(layoutCartPage);
    bodyObserver.observe(document.body, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
